import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { ArrowLeft, Store, ClipboardList, Receipt, BarChart3 } from 'lucide-react';
import API from '../lib/api';
import LabsStoreCatalogTab from '../components/labsstore/LabsStoreCatalogTab';
import LabsStoreBookingsTab from '../components/labsstore/LabsStoreBookingsTab';
import LabsStoreLedgerTab from '../components/labsstore/LabsStoreLedgerTab';
import LabsStoreDashboardTab from '../components/labsstore/LabsStoreDashboardTab';

export default function DoctorLabsStorePage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('catalog');
  const [feed, setFeed] = useState({ items: [], banners: [], popup: null });
  const [feedLoading, setFeedLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [bookingsFilters, setBookingsFilters] = useState({ search: '', date_from: '', date_to: '' });
  const [dashboardFilters, setDashboardFilters] = useState({ date_from: '', date_to: '' });

  const fetchFeed = useCallback(async () => {
    setFeedLoading(true);
    try {
      const res = await API.get('/labs-store/featured?audience=doctor');
      setFeed(res.data);
    } catch (err) {
      // keep empty state
    } finally {
      setFeedLoading(false);
    }
  }, []);

  const fetchBookings = useCallback(async (filters = bookingsFilters) => {
    setBookingsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.set('search', filters.search);
      if (filters.date_from) params.set('date_from', filters.date_from);
      if (filters.date_to) params.set('date_to', filters.date_to);
      const res = await API.get(`/labs-store/doctor/bookings?${params.toString()}`);
      setBookings(res.data);
    } catch (err) {
      // keep empty state
    } finally {
      setBookingsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchVendors = useCallback(async () => {
    try {
      const res = await API.get('/labs-store/vendors');
      setVendors(res.data);
    } catch (err) {
      // keep empty state
    }
  }, []);

  const fetchDashboard = useCallback(async (filters = dashboardFilters) => {
    setDashboardLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.date_from) params.set('date_from', filters.date_from);
      if (filters.date_to) params.set('date_to', filters.date_to);
      if (filters.search) params.set('search', filters.search);
      const res = await API.get(`/labs-store/doctor/dashboard?${params.toString()}`);
      setDashboard(res.data);
    } catch (err) {
      // keep empty state
    } finally {
      setDashboardLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyBookingsFilters = (filters) => {
    setBookingsFilters(filters);
    fetchBookings(filters);
  };

  const applyDashboardFilters = (filters) => {
    setDashboardFilters(filters);
    fetchDashboard(filters);
  };

  useEffect(() => {
    fetchFeed();
    fetchBookings();
    fetchVendors();
    fetchDashboard();
    // Refresh bookings after returning from a PayU due-payment redirect
    if (new URLSearchParams(window.location.search).get('payu_return') === 'labs_store_due') {
      setTimeout(() => { fetchBookings(); fetchDashboard(); }, 1200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchFeed, fetchBookings, fetchVendors, fetchDashboard]);

  return (
    <Layout>
      <div className="space-y-6" data-testid="doctor-labs-store-page">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/doctor/dashboard')} data-testid="labs-store-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Labs & Store</h1>
            <p className="text-sm text-slate-500">Book tests, buy products, and track your earnings</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="catalog" data-testid="labs-store-tab-catalog"><Store className="w-4 h-4 mr-1.5" /> Store</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="labs-store-tab-bookings"><ClipboardList className="w-4 h-4 mr-1.5" /> My Bookings</TabsTrigger>
            <TabsTrigger value="ledger" data-testid="labs-store-tab-ledger"><Receipt className="w-4 h-4 mr-1.5" /> Ledger</TabsTrigger>
            <TabsTrigger value="dashboard" data-testid="labs-store-tab-dashboard"><BarChart3 className="w-4 h-4 mr-1.5" /> Dashboard</TabsTrigger>
          </TabsList>

          <TabsContent value="catalog">
            <LabsStoreCatalogTab items={feed.items} banners={feed.banners} popup={feed.popup} loading={feedLoading} onPurchased={() => { fetchBookings(); fetchDashboard(); }} />
          </TabsContent>
          <TabsContent value="bookings">
            <LabsStoreBookingsTab
              bookings={bookings}
              loading={bookingsLoading}
              onRefresh={() => fetchBookings(bookingsFilters)}
              filters={bookingsFilters}
              onFiltersChange={applyBookingsFilters}
            />
          </TabsContent>
          <TabsContent value="ledger">
            <LabsStoreLedgerTab vendors={vendors} />
          </TabsContent>
          <TabsContent value="dashboard">
            <LabsStoreDashboardTab
              dashboard={dashboard}
              loading={dashboardLoading}
              filters={dashboardFilters}
              onFiltersChange={applyDashboardFilters}
            />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
